package fr.frux.core.maps

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.dsl.Feature
import org.maplibre.compose.expressions.dsl.all
import org.maplibre.compose.expressions.dsl.asNumber
import org.maplibre.compose.expressions.dsl.case
import org.maplibre.compose.expressions.dsl.condition
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.gte
import org.maplibre.compose.expressions.dsl.image
import org.maplibre.compose.expressions.dsl.lt
import org.maplibre.compose.expressions.dsl.lte
import org.maplibre.compose.expressions.dsl.min
import org.maplibre.compose.expressions.dsl.offset
import org.maplibre.compose.expressions.dsl.plus
import org.maplibre.compose.expressions.dsl.switch
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.expressions.value.FloatValue
import org.maplibre.compose.layers.SymbolLayer
import org.maplibre.compose.sources.GeoJsonOptions
import org.maplibre.compose.sources.VectorSource
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

/**
 * Pastille qu'un marqueur fait apparaitre autour du regroupement qui le contient (#710) : un
 * glyphe et sa teinte. Volontairement sans vocabulaire metier, comme [MapMarker.tint] : l'appelant
 * y met l'espece et sa couleur de famille, `core-maps` ne sait que les dessiner. Deux marqueurs au
 * meme badge comptent pour la meme pastille.
 */
data class MarkerBadge(
    val iconKey: String,
    val tint: Color,
)

/** Au-dela, les badges les moins representes ne se montrent pas, et rien ne le signale. */
internal const val MAX_CLUSTER_SATELLITES = 8

/**
 * Diametre d'un satellite, bord compris. Releve de 20 a 26 dp apres la premiere passe (#710) :
 * a 20, la couronne paraissait chetive a cote de la pastille de 40.
 */
internal val SATELLITE_SIZE: Dp = 26.dp

internal val SATELLITE_RIM_WIDTH: Dp = 2.dp

/**
 * Glyphe plus gros, en proportion, que sur le marqueur : a la part du marqueur (20/36), il ne
 * ferait que 9 dp sur la plaque de 16 dp et se lirait comme une tache.
 */
internal const val SATELLITE_GLYPH_FRACTION = 0.7f

/**
 * Distance du centre du regroupement au centre de chaque satellite : derivee des deux tailles pour
 * que chaque satellite reste tangent a la pastille, quoi qu'on regle.
 */
internal val SATELLITE_ORBIT: Dp = CLUSTER_SIZE / 2 + SATELLITE_SIZE / 2

/**
 * Plafond de badges distincts suivis par une carte : chacun coute un agregat a la source et une
 * comparaison par paire dans le classement.
 */
internal const val BADGE_SLOTS = 128

private const val BADGE_PROPERTY = "badge"

internal fun badgeCountProperty(index: Int): String = "badge_$index"

internal fun badgeKey(badge: MarkerBadge): String = "${badge.iconKey}|${badge.tint.toCssHex()}"

/**
 * Badges connus de la carte, dans leur ordre d'arrivee. Ne retrecit jamais : les agregats de
 * regroupement font partie des options de la source, et changer d'options recree la source. Un lot
 * d'arbres qui n'apporte aucun badge nouveau ne touche donc pas a la source.
 */
internal class BadgeRegistry {
    private val indices = LinkedHashMap<String, Int>()
    private val badges = mutableListOf<MarkerBadge>()

    /** Enregistre les badges inconnus et rend la liste complete, indices compris. */
    fun register(markers: List<MapMarker>): List<MarkerBadge> {
        for (badge in markers.mapNotNull { it.badge }) {
            if (indices.size >= BADGE_SLOTS) break
            val key = badgeKey(badge)
            if (key !in indices) {
                indices[key] = badges.size
                badges += badge
            }
        }
        return badges.toList()
    }

    fun indexOf(badge: MarkerBadge?): Int? = badge?.let { indices[badgeKey(it)] }
}

/**
 * Centre du satellite de rang [rank] (0 = le plus represente) parmi [count], relatif au centre du
 * regroupement. Le premier se pose au sommet, les suivants dans le sens horaire a pas egal : un
 * seul badge, un satellite en haut, huit, une couronne complete.
 */
internal fun satelliteOffset(
    rank: Int,
    count: Int,
): DpOffset {
    val angle = -PI / 2 + rank * 2 * PI / count
    return DpOffset(SATELLITE_ORBIT * cos(angle).toFloat(), SATELLITE_ORBIT * sin(angle).toFloat())
}

/** Agregats de la source : l'effectif de chaque badge connu dans chaque regroupement. */
internal fun badgeClusterProperties(badgeCount: Int): Map<String, GeoJsonOptions.ClusterPropertyAggregator<*>> =
    (0 until badgeCount).associate { index ->
        val property = badgeCountProperty(index)
        property to
            GeoJsonOptions.ClusterPropertyAggregator(
                mapper =
                    switch(
                        condition(
                            test = Feature[BADGE_PROPERTY].asNumber(const(-1f)).eqNumber(index),
                            output = const(1f),
                        ),
                        fallback = const(0f),
                    ),
                reducer = Feature.accumulated().asNumber(const(0f)) + Feature[property].asNumber(const(0f)),
            )
    }

private fun Expression<FloatValue>.eqNumber(value: Int): Expression<BooleanValue> =
    all(this gte const(value.toFloat()), this lt const(value + 1f))

/** Propriete GeoJSON portant l'indice de badge d'un marqueur, lue par les agregats. */
internal const val BADGE_FEATURE_PROPERTY = BADGE_PROPERTY

/** Effectif agrege de chaque badge connu, 0 quand le regroupement n'en contient pas. */
private fun badgeCounts(badgeCount: Int): List<Expression<FloatValue>> =
    (0 until badgeCount).map { index -> Feature[badgeCountProperty(index)].asNumber(const(0f)) }

/**
 * Rang du badge [index] dans le regroupement : le nombre de badges qui le devancent. A effectif
 * egal, l'indice le plus grand passe devant, regle connue des la construction : `>=` contre un
 * indice superieur, `>` contre un indice inferieur. Deux badges n'ont donc jamais le meme rang.
 *
 * Ni variable ni chaine de maxima, et ce n'est pas du style. Une `var` MapLibre n'est pas memorisee
 * (chaque reference reevalue l'expression liee), et maplibre-gl descend deux fois dans le corps de
 * chaque `let` en cherchant une courbe de zoom : des `let` imbriques, un par espece, ou des maxima
 * successifs relisant chacun le precedent, coutaient un temps exponentiel et figeaient la carte web
 * des qu'un quartier comptait une trentaine d'especes. Ici, N comparaisons de proprietes.
 */
private fun rankOf(
    counts: List<Expression<FloatValue>>,
    index: Int,
): Expression<FloatValue> {
    val ahead =
        counts.indices
            .filter { it != index }
            .map { other ->
                val isAhead = if (other > index) counts[index] lte counts[other] else counts[index] lt counts[other]
                switch(condition(test = isAhead, output = const(1f)), fallback = const(0f))
            }
    return if (ahead.isEmpty()) const(0f) else ahead.reduce { acc, next -> acc + next }
}

private fun isPresent(count: Expression<FloatValue>): Expression<BooleanValue> = count gte const(1f)

/** Nombre de badges presents, plafonne a [MAX_CLUSTER_SATELLITES]. */
private fun presentCount(counts: List<Expression<FloatValue>>): Expression<FloatValue> =
    min(
        counts
            .map { count -> switch(condition(test = isPresent(count), output = const(1f)), fallback = const(0f)) }
            .reduce { acc, next -> acc + next },
        const(MAX_CLUSTER_SATELLITES.toFloat()),
    )

/**
 * Satellites de badges autour des regroupements (#710) : une couche de symboles par rang, chaque
 * regroupement y montrant, s'il l'a, son badge de ce rang. Le classement se fait dans l'expression,
 * a partir des agregats de la source : il suit donc le zoom image par image, sans aller-retour par
 * Kotlin. A poser sous la pastille de regroupement, pour que la couronne d'un voisin ne masque
 * jamais un compteur.
 */
@Composable
internal fun ClusterSatelliteLayers(
    source: VectorSource,
    badges: List<MarkerBadge>,
    markerIcons: Map<String, Painter>,
    fallbackGlyph: Painter?,
    clusterFilter: Expression<BooleanValue>,
    opacity: Float = 1f,
) {
    if (badges.isEmpty()) return
    val painters =
        remember(badges, markerIcons, fallbackGlyph) {
            badges.map { badge -> satellitePainter(badge, markerIcons, fallbackGlyph) }
        }
    val counts = badgeCounts(badges.size)
    repeat(MAX_CLUSTER_SATELLITES) { rank ->
        SymbolLayer(
            id = "frux-trees-cluster-satellite-$rank",
            source = source,
            filter =
                all(
                    clusterFilter,
                    // Ce rang existe si le regroupement compte plus de `rank` badges : un
                    // comptage lineaire, sans classement.
                    presentCount(counts) gte const(rank + 1f),
                ),
            // Le badge present dont le rang vaut `rank`. La presence se teste d'abord : `all`
            // s'arrete au premier faux, et la plupart des badges connus sont absents d'un
            // regroupement donne, ils ne coutent alors qu'une lecture.
            iconImage =
                switch(
                    *painters
                        .indices
                        .map { index ->
                            condition(
                                test = all(isPresent(counts[index]), rankOf(counts, index).eqNumber(rank)),
                                output = image(painters[index], size = DpSize(SATELLITE_SIZE, SATELLITE_SIZE)),
                            )
                        }.toTypedArray(),
                    fallback = image(painters.first(), size = DpSize(SATELLITE_SIZE, SATELLITE_SIZE)),
                ),
            iconOffset =
                switch(
                    input = presentCount(counts),
                    *(rank + 1..MAX_CLUSTER_SATELLITES)
                        .map { count ->
                            val position = satelliteOffset(rank, count)
                            case(count, offset(position.x, position.y))
                        }.toTypedArray(),
                    fallback = offset(0.dp, 0.dp),
                ),
            iconOpacity = const(opacity),
            iconAllowOverlap = const(true),
        )
    }
}
